import { useEffect, useRef } from "react";

export function CustomCursor() {
  const dotRef = useRef(null);
  const circleRef = useRef(null);
  const mouse = useRef({ x: -100, y: -100 });
  const circlePos = useRef({ x: -100, y: -100 });
  const raf = useRef(null);

  useEffect(() => {
    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
    if (isTouchDevice) return;

    const dot = dotRef.current;
    const circle = circleRef.current;
    if (!dot || !circle) return;

    const onMouseMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
      dot.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    };

    const onMouseEnterInteractive = () => {
      circle.style.width = "44px";
      circle.style.height = "44px";
      circle.style.borderColor = "rgba(158, 138, 90, 0.5)";
    };

    const onMouseLeaveInteractive = () => {
      circle.style.width = "30px";
      circle.style.height = "30px";
      circle.style.borderColor = "rgba(158, 138, 90, 0.25)";
    };

    const animate = () => {
      const ease = 0.08;
      circlePos.current.x += (mouse.current.x - circlePos.current.x) * ease;
      circlePos.current.y += (mouse.current.y - circlePos.current.y) * ease;
      circle.style.transform = `translate(${circlePos.current.x}px, ${circlePos.current.y}px)`;
      raf.current = requestAnimationFrame(animate);
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    raf.current = requestAnimationFrame(animate);

    const interactives = document.querySelectorAll("a, button, [role='button'], select, input, textarea, label[for]");
    interactives.forEach((el) => {
      el.addEventListener("mouseenter", onMouseEnterInteractive);
      el.addEventListener("mouseleave", onMouseLeaveInteractive);
    });

    // Re-observe DOM for dynamically added elements
    const mutObs = new MutationObserver(() => {
      const newInteractives = document.querySelectorAll("a, button, [role='button'], select, input, textarea, label[for]");
      newInteractives.forEach((el) => {
        el.removeEventListener("mouseenter", onMouseEnterInteractive);
        el.removeEventListener("mouseleave", onMouseLeaveInteractive);
        el.addEventListener("mouseenter", onMouseEnterInteractive);
        el.addEventListener("mouseleave", onMouseLeaveInteractive);
      });
    });
    mutObs.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      cancelAnimationFrame(raf.current);
      mutObs.disconnect();
      interactives.forEach((el) => {
        el.removeEventListener("mouseenter", onMouseEnterInteractive);
        el.removeEventListener("mouseleave", onMouseLeaveInteractive);
      });
    };
  }, []);

  return (
    <>
      {/* 1px dot */}
      <div
        ref={dotRef}
        className="fixed top-0 left-0 z-[9999] pointer-events-none"
        style={{
          width: "3px",
          height: "3px",
          marginLeft: "-1.5px",
          marginTop: "-1.5px",
          backgroundColor: "var(--text-primary)",
          borderRadius: "50%",
          willChange: "transform",
        }}
      />
      {/* Trailing gold circle */}
      <div
        ref={circleRef}
        className="fixed top-0 left-0 z-[9998] pointer-events-none"
        style={{
          width: "30px",
          height: "30px",
          marginLeft: "-15px",
          marginTop: "-15px",
          border: "1px solid rgba(158, 138, 90, 0.25)",
          borderRadius: "50%",
          willChange: "transform",
          transition: "width 0.3s ease, height 0.3s ease, border-color 0.3s ease, margin 0.3s ease",
        }}
      />
    </>
  );
}
